import React from 'react';
import {
  ADMIN_USER_TYPE,
  ADMIN_VIEW_USER_TYPE,
  allOption,
  CONFIG_ADMIN_USER_TYPE,
  CONFIG_ADMIN_VIEW_USER_TYPE,
  KWH,
  MAPPER_UP_AND_DOWN_ASC_DESC,
  MEMBER_USER_TYPE,
  MEMBER_VIEW_USER_TYPE,
  NO_DATA,
  RP_ADDRESS,
  RP_CONFIGURATION,
  RP_CONTEMPOREANITY,
  RP_EC_ID,
  RP_ENERGY_CONSUMPTION,
  RP_ENERGY_DISPATCHED,
  RP_ENERGY_SHARED,
  RP_PARTECIPANT_NAME,
  RP_POD,
  RP_POD_NAME,
  RP_SHARED_RATIO,
  RP_TIME_PERIOD,
  RP_TOTAL_REVENUE,
} from 'consts';
import useFormatter from 'hook/useFormatter';
import { useFilterHook, useWidgetHook } from 'hook/usePageConfig';
import useTableRender from 'hook/useTableRender';
import moment from 'moment';

export const allSearchBarFilters = ({
  t, epoch, energyCommunities, filters, setFilters, currentTemplate, setCurrentTemplate, userType,
}) => {
  const {
    currentTimeZone,
  } = epoch ?? {};

  const {
    energyCommunityFilter: selectedEc,
    configurationFilter: configSelected,
    podFilter: podSelected,
  } = filters;

  const {
    configOptions,
    dateFilter,
    memberArr,
    podOptions,
  } = useFilterHook({
    energyCommunities,
    selectedEc,
    configSelected,
    podSelected,
    t,
    epoch,
    currentTimeZone,
    setFilters,
    filters,
    currentTemplate,
    setCurrentTemplate,
  });

  let toRet = [];
  if (userType === ADMIN_USER_TYPE || userType === ADMIN_VIEW_USER_TYPE) {
    configOptions.unshift(allOption(t));
    toRet = [
      {
        id: RP_CONFIGURATION,
        type: 'select',
        label: t('filters.configuration'),
        options: configOptions,
        disableChipClose: true,
        defaultValue: 'all',
      },
      dateFilter,
    ];
  }

  if (userType === CONFIG_ADMIN_USER_TYPE || userType === CONFIG_ADMIN_VIEW_USER_TYPE) {
    podOptions.unshift(allOption(t));
    toRet = [
      {
        id: RP_CONFIGURATION,
        type: 'select',
        label: t('filters.configuration'),
        options: configOptions,
        disableChipClose: true,
        onSelect: (value) => {
          setFilters({
            ...filters,
            configurationFilter: value,
          });
          currentTemplate.searchbar.filtersValue = [
            {
              id: RP_EC_ID,
              value: selectedEc,
            },
            {
              id: RP_CONFIGURATION,
              value,
            },
          ];
          setCurrentTemplate(currentTemplate);
        },
        defaultValue: configSelected,
      },
      {
        id: RP_POD,
        type: 'select',
        label: t('filters.loadArea'),
        options: podOptions,
        disableChipClose: true,
        defaultValue: 'all',
      },
      dateFilter,
    ];
  }

  if (userType === MEMBER_USER_TYPE || userType === MEMBER_VIEW_USER_TYPE) {
    toRet = memberArr;
  }

  return toRet;
};

export const allTableColumns = ({
  t, countryID, userType, setSorter,
}) => {
  const {
    defaultColumnRender,
    defaultCurrencyRender,
  } = useTableRender();

  const {
    unit: { formatElectricEnergy },
  } = useFormatter();

  const healthColorMap = {
    BAD: '210, 25, 49',
    MEDIUM: '251, 211, 132',
    GOOD: '2, 119, 166',
    EXCELLENT: '27, 125, 97',
  };

  const healthColorValue = (data) => (data
    ? healthColorMap[data] : undefined);

  let firstColumn = {};
  let addressRender = () => {};
  let addressSorter = '';
  let lastColumn = {};
  if (userType === ADMIN_USER_TYPE || userType === ADMIN_VIEW_USER_TYPE) {
    firstColumn = {
      id: RP_PARTECIPANT_NAME,
      label: t('columns.configuration'),
      render: (_data, _index, dto) => defaultColumnRender(dto?.dto?.configurationName ?? NO_DATA),
      sortable: (sort) => setSorter(
        { sort: 'configurationName', sortType: MAPPER_UP_AND_DOWN_ASC_DESC[sort.direction] },
      ),
    };
    addressRender = (_data, _index, dto) => defaultColumnRender(dto?.dto?.configurationAddress ?? NO_DATA);
    addressSorter = 'configurationAddress';
    lastColumn = {
      id: RP_SHARED_RATIO,
      label: t('columns.condivision'),
      render: (_data, _index, dto) => (
        <div
          className="rounded border-2 p-2"
          style={{
            borderColor: `rgba(${healthColorValue(dto?.dto?.monthlyHealth)})`,
            backgroundColor: `rgba(${healthColorValue(dto?.dto?.monthlyHealth)}, 0.1)`,
          }}
        >
          {`${dto?.dto?.monthlySaturation?.toFixed(2).replace('.', ',') ?? NO_DATA} %`}
        </div>
      ),
      sortable: (sort) => setSorter(
        { sort: 'monthlySaturation', sortType: MAPPER_UP_AND_DOWN_ASC_DESC[sort.direction] },
      ),
    };
  }
  if (userType === CONFIG_ADMIN_USER_TYPE || userType === CONFIG_ADMIN_VIEW_USER_TYPE
    || userType === MEMBER_USER_TYPE || userType === MEMBER_VIEW_USER_TYPE) {
    addressRender = (_data, _index, dto) => defaultColumnRender(dto?.dto?.memberAddress ?? NO_DATA);
    addressSorter = 'memberAddress';
    lastColumn = {
      id: RP_CONTEMPOREANITY,
      label: t('columns.contemporaneity'),
      render: (_data, _index, dto) => (
        <div
          className="rounded border-2 p-2"
          style={{
            borderColor: `rgba(${healthColorValue(dto?.dto?.monthlySolarHappiness)})`,
            backgroundColor: `rgba(${healthColorValue(dto?.dto?.monthlySolarHappiness)}, 0.1)`,
          }}
        >
          {`${dto?.dto?.monthlyContemporaneityFactor?.toFixed(2).replace('.', ',') ?? NO_DATA} %`}
        </div>
      ),
      sortable: (sort) => setSorter(
        { sort: 'monthlyContemporaneityFactor', sortType: MAPPER_UP_AND_DOWN_ASC_DESC[sort.direction] },
      ),
    };
  }

  if (userType === CONFIG_ADMIN_USER_TYPE || userType === CONFIG_ADMIN_VIEW_USER_TYPE) {
    firstColumn = {
      id: RP_PARTECIPANT_NAME,
      label: t('columns.pod'),
      render: (_data, _index, dto) => defaultColumnRender(dto?.dto?.memberName ?? NO_DATA),
      sortable: (sort) => setSorter(
        { sort: 'memberName', sortType: MAPPER_UP_AND_DOWN_ASC_DESC[sort.direction] },
      ),
    };
  }
  if (userType === MEMBER_USER_TYPE || userType === MEMBER_VIEW_USER_TYPE) {
    firstColumn = {
      id: RP_POD_NAME,
      label: t('columns.pod'),
      render: (_data, _index, dto) => defaultColumnRender(dto?.dto?.memberName ?? NO_DATA),
      sortable: (sort) => setSorter(
        { sort: 'memberName', sortType: MAPPER_UP_AND_DOWN_ASC_DESC[sort.direction] },
      ),
    };
  }

  return [
    firstColumn,
    {
      id: RP_TIME_PERIOD,
      label: t('columns.timePeriod'),
      render: (_data, _index, dto) => defaultColumnRender(moment(dto?.dto?.monthDate).locale(countryID).format('MMMM YYYY')),
      sortable: (sort) => setSorter(
        { sort: 'monthDate', sortType: MAPPER_UP_AND_DOWN_ASC_DESC[sort.direction] },
      ),
    },
    {
      id: RP_ADDRESS,
      label: t('columns.energyCommunity'),
      render: addressRender,
      sortable: (sort) => setSorter(
        { sort: addressSorter, sortType: MAPPER_UP_AND_DOWN_ASC_DESC[sort.direction] },
      ),
    },
    {
      id: RP_ENERGY_CONSUMPTION,
      label: t('columns.energyConsumption'),
      render: (_data, _index, dto) => defaultColumnRender(formatElectricEnergy(dto?.dto?.monthlyConsumption, KWH)),
      sortable: (sort) => setSorter(
        { sort: 'monthlyConsumption', sortType: MAPPER_UP_AND_DOWN_ASC_DESC[sort.direction] },
      ),
    },
    {
      id: RP_ENERGY_DISPATCHED,
      label: t('columns.dispatchedEnergy'),
      render: (_data, _index, dto) => defaultColumnRender(formatElectricEnergy(dto?.dto?.monthlyGeneration, KWH)),
      sortable: (sort) => setSorter(
        { sort: 'monthlyGeneration', sortType: MAPPER_UP_AND_DOWN_ASC_DESC[sort.direction] },
      ),
    },
    {
      id: RP_ENERGY_SHARED,
      label: t('columns.sharedEnergy'),
      render: (_data, _index, dto) => defaultColumnRender(formatElectricEnergy(dto?.dto?.monthlyShared, KWH)),
      sortable: (sort) => setSorter(
        { sort: 'monthlyShared', sortType: MAPPER_UP_AND_DOWN_ASC_DESC[sort.direction] },
      ),
    },
    {
      id: RP_TOTAL_REVENUE,
      label: t('columns.rewards'),
      render: (_data, _index, dto) => defaultCurrencyRender(dto?.dto?.monthlyRevenue),
      sortable: (sort) => setSorter(
        { sort: 'monthlyRevenue', sortType: MAPPER_UP_AND_DOWN_ASC_DESC[sort.direction] },
      ),
    },
    lastColumn,
  ];
};

export const allWidgetStructure = ({
  t, widgetsData, theme, isLoading,
  countryID, granularity, setGranularity,
  dataChartType, setDataChartType, userType, podValue, isDashboard, isDateDiffUnder30Days,
}) => {
  const {
    rewardsWidget,
    energyProductionWidget,
    energyRanking,
  } = useWidgetHook({
    t,
    widgetsData,
    theme,
    isLoading,
    countryID,
    granularity,
    setGranularity,
    dataChartType,
    setDataChartType,
    userType,
    podValue,
    isDashboard,
    isDateDiffUnder30Days,
  });

  let toRet = [];

  if (userType === ADMIN_USER_TYPE || userType === ADMIN_VIEW_USER_TYPE
    || userType === CONFIG_ADMIN_USER_TYPE || userType === CONFIG_ADMIN_VIEW_USER_TYPE) {
    toRet = [rewardsWidget, energyProductionWidget];
  }

  if (userType === MEMBER_USER_TYPE || userType === MEMBER_VIEW_USER_TYPE) {
    toRet = [rewardsWidget, energyProductionWidget, energyRanking];
  }

  return toRet;
};
